// One note in a list — read, edit in place, delete with a confirm.
//
// Moved out of EntityNotesCard.tsx unchanged (S13) when the notes
// composer became an OS-wide bottom dock. It is deliberately a verbatim
// move: the edit/delete semantics here (author-or-system_admin, mirrored
// server-side; "edited" derived from a >1s gap between created_at and
// updated_at; delete behind a confirm that says the note is restorable)
// have been in production since 2026-08-05 and none of them are what
// S13 set out to change.

import { useState, type CSSProperties } from "react";
import { Pencil, Pin, Star, Trash2 } from "lucide-react";

import { api, ApiError, type Note } from "../../api";
import type { NoteEntitySegment } from "@shared/types/notes";
import { Button } from "../../ui";

const noteActionStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--mid-gray)",
  background: "var(--bg-0)",
  color: "var(--navy)",
  borderRadius: 6,
  padding: 4,
  cursor: "pointer",
};

export function NoteRow({
  note,
  kind,
  entityId,
  canModify,
  onUpdated,
  onDeleted,
}: {
  note: Note;
  kind: NoteEntitySegment;
  entityId: string;
  canModify: boolean;
  onUpdated: (note: Note) => void;
  onDeleted: (noteId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const wasEdited =
    new Date(note.updated_at).getTime() - new Date(note.created_at).getTime() > 1000;

  async function saveEdit() {
    const body = draft.trim();
    if (body.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.updateEntityNote(kind, entityId, note.id, { body });
      onUpdated(res.note);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save the edit.");
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteEntityNote(kind, entityId, note.id);
      onDeleted(note.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete the note.");
      setBusy(false);
    }
  }

  return (
    <li
      className={`household-tab-note${note.pinned ? " is-pinned" : ""}${note.important ? " is-important" : ""}`}
    >
      <div className="household-tab-note-header">
        {note.pinned && (
          <span className="household-tab-note-pill">
            <Pin size={14} strokeWidth={1.5} aria-hidden /> Pinned
          </span>
        )}
        {note.important && (
          <span className="household-tab-note-pill is-important">
            <Star size={14} strokeWidth={1.5} fill="currentColor" aria-hidden /> Important
          </span>
        )}
        {note.title && <strong className="household-tab-note-title">{note.title}</strong>}
        {canModify && !editing && (
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
            <button
              type="button"
              onClick={() => {
                setDraft(note.body);
                setEditing(true);
                setError(null);
              }}
              title="Edit this note"
              aria-label="Edit note"
              className="household-tab-note-action"
              style={noteActionStyle}
            >
              <Pencil size={14} strokeWidth={1.6} aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              title="Delete this note"
              aria-label="Delete note"
              className="household-tab-note-action"
              style={noteActionStyle}
            >
              <Trash2 size={14} strokeWidth={1.6} aria-hidden />
            </button>
          </span>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={8000}
            style={{
              width: "100%",
              fontSize: 13,
              padding: 8,
              border: "1px solid var(--mid-gray)",
              borderRadius: "var(--radius-md)",
              resize: "vertical",
            }}
          />
          {error && <div style={{ fontSize: 12, color: "var(--danger)" }}>{error}</div>}
          <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
            <Button size="sm" variant="sw-primary" disabled={busy} onClick={() => void saveEdit()}>
              {busy ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="household-tab-note-body">{note.body}</div>
          <div className="household-tab-note-meta">
            {note.author_name ?? "Unknown author"} · {new Date(note.created_at).toLocaleString()}
            {wasEdited && " · edited"}
          </div>
          {error && <div style={{ fontSize: 12, color: "var(--danger)" }}>{error}</div>}
        </>
      )}
      {confirmDelete && (
        <div style={{ marginTop: 6, fontSize: 12.5 }}>
          Delete this note? It can be restored from the record's history.{" "}
          <Button size="sm" variant="sw-default" disabled={busy} onClick={() => void doDelete()}>
            {busy ? "Deleting…" : "Delete"}
          </Button>{" "}
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmDelete(false)}>
            Cancel
          </Button>
        </div>
      )}
    </li>
  );
}
